import { useEffect, useState, useCallback, useMemo } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, LayerGroup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { Skull, RefreshCw, X, ExternalLink, Maximize2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import MapLegend from '../components/MapLegend'
import { countryFlag, countryName } from '../lib/countryFlag'

interface Point {
  country: string
  lat: number
  lng: number
  count: number
  top_sectors: { sector: string; count: number }[]
}

interface GeoData {
  points: Point[]
  total_victims: number
  by_country: { country: string; count: number }[]
  by_sector:  { sector: string; count: number }[]
  days: number
}

interface SupplyHit  { service_id: number; service_name: string; match_kind: string; matched_on?: string[] }
interface CarrierHit { carrier: string; carrier_name: string; title: string; url: string; published_at: string | null }
interface Victim {
  org_name: string
  group: string
  sector: string | null
  discovered_at: string | null
  leak_url: string | null
  description: string | null
  supply_chain_hits:  SupplyHit[]
  carrier_advisories: CarrierHit[]
}
interface CountryDetail {
  country: string
  country_name: string
  days: number
  total_victims: number
  victims: Victim[]
  by_group:  { group: string; victims: number }[]
  by_sector: { sector: string; victims: number }[]
  timeline:  { date: string; victims: number }[]
}

export default function RansomwareGeoPage() {
  const [data, setData] = useState<GeoData | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)
  const [activeCountry, setActiveCountry] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/overlay/ransomware-geo', { params: { days } })
      setData(r.data)
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { fetchAll() }, [fetchAll])

  const maxCount = Math.max(1, ...(data?.points?.map(p => p.count) || []))

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Skull size={24} className="text-purple-300" /> Ransomware Geo + Sector
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Recent ransomware victims plotted by country, with top sectors per country. Source: ransomware.live.
            Click a circle for the full victim list + supply-chain / carrier-advisory cross-links.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            aria-label="Time window"
            title="Time window"
            value={days}
            onChange={e => setDays(parseInt(e.target.value, 10))}
            className="input py-1.5 text-xs"
          >
            <option value={7}>7d</option><option value={14}>14d</option><option value={30}>30d</option><option value={60}>60d</option><option value={90}>90d</option>
          </select>
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs" aria-label="Refresh" title="Refresh">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Victims (window)" value={data.total_victims} tone="rose" />
          <Tile label="Countries hit" value={data.points.length} tone="amber" />
          <Tile label="Top country"  value={data.by_country[0]?.count || 0} sub={data.by_country[0]?.country || '—'} tone="rose" />
          <Tile label="Top sector"   value={data.by_sector[0]?.count || 0} sub={data.by_sector[0]?.sector || '—'} tone="amber" />
        </div>
      )}

      {/* Map */}
      <div className="card p-0 overflow-hidden relative" style={{ height: 480 }}>
        <MapLegend
          floating
          corner="bottom-left"
          title="Legend"
          entries={[
            { hex: '#10b981', label: 'Victim country (radius ∝ victim count) — click to drill down' },
          ]}
        />
        <MapContainer center={[20, 0]} zoom={2} minZoom={2} style={{ height: '100%', width: '100%', background: '#0b1020' }}>
          <TileLayer url="/api/tiles/{z}/{x}/{y}.png" attribution='&copy; CARTO (proxied)' />
          <LayerGroup>
            {data?.points.map(p => {
              const radius = 6 + Math.sqrt(p.count / maxCount) * 22
              return (
                <CircleMarker key={p.country} center={[p.lat, p.lng]} radius={radius}
                  pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 0.4, weight: 2 }}>
                  <Popup>
                    <div className="text-xs">
                      <strong>{countryFlag(p.country)} {countryName(p.country)}</strong> — {p.count} victim{p.count !== 1 ? 's' : ''}
                      <br/>
                      {p.top_sectors.length > 0 && (
                        <span className="text-[11px] text-text-muted">
                          Top sectors: {p.top_sectors.slice(0, 3).map(s => s.sector).join(', ')}
                          <br/>
                        </span>
                      )}
                      <button
                        type="button"
                        className="mt-1 text-blue-light hover:underline font-semibold"
                        onClick={() => setActiveCountry(p.country)}
                      >
                        View {p.count} victim{p.count !== 1 ? 's' : ''} →
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              )
            })}
          </LayerGroup>
        </MapContainer>
      </div>

      {/* Side-by-side breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="card p-3">
          <h3 className="text-xs font-semibold text-text-primary mb-2">Top countries hit</h3>
          {data?.by_country.length === 0 ? (
            <p className="text-[10px] text-text-muted italic">No data.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {data?.by_country.slice(0, 12).map(c => (
                <li key={c.country} className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setActiveCountry(c.country)}
                    className="font-mono text-text-primary hover:text-blue-light hover:underline text-left"
                  >
                    {countryFlag(c.country)} {countryName(c.country)}
                  </button>
                  <span className="font-mono font-bold text-rose">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card p-3">
          <h3 className="text-xs font-semibold text-text-primary mb-2">Top sectors hit</h3>
          {data?.by_sector.length === 0 ? (
            <p className="text-[10px] text-text-muted italic">No data.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {data?.by_sector.slice(0, 12).map(s => (
                <li key={s.sector} className="flex items-center justify-between">
                  <span className="text-text-secondary truncate max-w-[260px]" title={s.sector}>{s.sector}</span>
                  <span className="font-mono font-bold text-amber-light">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {activeCountry && (
        <CountryDrawer
          iso2={activeCountry}
          days={days}
          onClose={() => setActiveCountry(null)}
        />
      )}
    </div>
  )
}

function Tile({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone: string }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t)}>{value.toLocaleString()}</div>
      {sub && <div className="text-[10px] text-text-muted mt-0.5">{sub}</div>}
    </div>
  )
}

// ── Drawer ──────────────────────────────────────────────────────────────────

function CountryDrawer({ iso2, days, onClose }: { iso2: string; days: number; onClose: () => void }) {
  const [detail, setDetail] = useState<CountryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [groupFilter,  setGroupFilter]  = useState<Set<string>>(new Set())
  const [sectorFilter, setSectorFilter] = useState<Set<string>>(new Set())
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get(`/api/overlay/ransomware-geo/country/${encodeURIComponent(iso2)}`, {
      params: { days, limit: 200 },
    }).then(r => { if (!cancelled) setDetail(r.data) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [iso2, days])

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const filteredVictims = useMemo(() => {
    if (!detail) return []
    return detail.victims.filter(v => {
      if (groupFilter.size  > 0 && !groupFilter.has(v.group))                 return false
      if (sectorFilter.size > 0 && !sectorFilter.has(v.sector || 'unknown')) return false
      return true
    })
  }, [detail, groupFilter, sectorFilter])

  const toggle = (set: Set<string>, key: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key); else next.add(key)
    setter(next)
  }

  return (
    <>
      {/* Backdrop — clicks anywhere outside the drawer close it. Pointer
          events are auto so the rest of the page is fully click-blocked
          while the drawer is open, but the drawer itself stops propagation. */}
      <div
        className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
        onClick={onClose}
      />
      <aside
        className="fixed top-0 bottom-0 right-0 z-50 bg-surface border-l border-border shadow-2xl overflow-y-auto animate-slide-in"
        style={{ width: 'clamp(360px, 40vw, 560px)', pointerEvents: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-surface border-b border-border px-4 py-3 flex items-center justify-between gap-2 z-10">
          <div className="min-w-0">
            <div className="text-lg font-bold text-text-primary truncate">
              {countryFlag(iso2)} {detail?.country_name || countryName(iso2)}
              <span className="ml-2 text-xs font-mono text-rose bg-rose/10 px-1.5 py-0.5 rounded">
                {detail?.total_victims ?? '…'} victim{detail?.total_victims === 1 ? '' : 's'}
              </span>
            </div>
            <div className="text-[10px] text-text-muted mt-0.5">Last {days} days</div>
          </div>
          <div className="flex items-center gap-1">
            <Link
              to={`/app/ransomware-geo/${iso2}`}
              className="btn-ghost text-[10px] flex items-center gap-1"
              title="Open full view"
            >
              <Maximize2 size={11} /> Full view
            </Link>
            <button type="button" onClick={onClose} className="btn-ghost text-xs" aria-label="Close drawer">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {loading && <p className="text-xs text-text-muted italic">Loading…</p>}
          {!loading && detail && detail.total_victims === 0 && (
            <p className="text-xs text-text-muted italic">No victims in this window.</p>
          )}

          {!loading && detail && detail.total_victims > 0 && (
            <>
              {/* Filter chips */}
              <div className="space-y-2">
                {detail.by_group.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Groups</div>
                    <div className="flex flex-wrap gap-1">
                      {detail.by_group.map(g => (
                        <button
                          key={g.group}
                          type="button"
                          onClick={() => toggle(groupFilter, g.group, setGroupFilter)}
                          className={clsx(
                            'text-[10px] font-mono px-1.5 py-0.5 rounded border',
                            groupFilter.has(g.group)
                              ? 'bg-amber/20 border-amber/50 text-amber-light'
                              : 'border-border text-text-secondary hover:bg-surface-hover',
                          )}
                        >
                          {g.group} <span className="opacity-60">({g.victims})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {detail.by_sector.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Sectors</div>
                    <div className="flex flex-wrap gap-1">
                      {detail.by_sector.slice(0, 12).map(s => (
                        <button
                          key={s.sector}
                          type="button"
                          onClick={() => toggle(sectorFilter, s.sector, setSectorFilter)}
                          className={clsx(
                            'text-[10px] px-1.5 py-0.5 rounded border truncate max-w-[180px]',
                            sectorFilter.has(s.sector)
                              ? 'bg-purple/20 border-purple/50 text-purple-light'
                              : 'border-border text-text-secondary hover:bg-surface-hover',
                          )}
                          title={s.sector}
                        >
                          {s.sector} <span className="opacity-60">({s.victims})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Victim table */}
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-surface-hover text-[10px] uppercase tracking-wider text-text-muted">
                    <tr>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-left px-2 py-1.5">Org</th>
                      <th className="text-left px-2 py-1.5">Group</th>
                      <th className="text-left px-2 py-1.5">Sector</th>
                      <th className="text-center px-2 py-1.5" title="Supply chain">SC</th>
                      <th className="text-center px-2 py-1.5" title="Carrier advisories">CA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVictims.map((v, i) => {
                      const expanded = expandedRow === i
                      return (
                        <FragmentRow
                          key={`${v.org_name}-${i}`}
                          victim={v}
                          expanded={expanded}
                          onToggle={() => setExpandedRow(expanded ? null : i)}
                        />
                      )
                    })}
                    {filteredVictims.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-[10px] italic text-text-muted py-3">No matches for current filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  )
}

function FragmentRow({ victim, expanded, onToggle }: { victim: Victim; expanded: boolean; onToggle: () => void }) {
  const date = victim.discovered_at ? victim.discovered_at.slice(0, 10) : '—'
  const sc = victim.supply_chain_hits.length
  const ca = victim.carrier_advisories.length
  return (
    <>
      <tr
        className="border-t border-border hover:bg-surface-hover cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">{date}</td>
        <td className="px-2 py-1.5 truncate max-w-[180px]" title={victim.org_name}>{victim.org_name || '—'}</td>
        <td className="px-2 py-1.5">
          <Link
            to={`/app/threat-actors?q=${encodeURIComponent(victim.group)}`}
            className="text-amber-light hover:underline font-mono text-[10px]"
            onClick={e => e.stopPropagation()}
          >
            {victim.group || 'unknown'}
          </Link>
        </td>
        <td className="px-2 py-1.5 truncate max-w-[120px] text-text-secondary text-[10px]" title={victim.sector || ''}>
          {victim.sector || '—'}
        </td>
        <td className="px-2 py-1.5 text-center">
          {sc > 0
            ? <span className="inline-block w-2 h-2 rounded-full bg-rose" title={victim.supply_chain_hits.map(h => h.service_name).join(', ')} />
            : <span className="text-text-muted opacity-40">·</span>}
        </td>
        <td className="px-2 py-1.5 text-center">
          {ca > 0
            ? <span className="inline-block w-2 h-2 rounded-full bg-purple" title={victim.carrier_advisories.map(h => h.title).join(', ')} />
            : <span className="text-text-muted opacity-40">·</span>}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-surface-hover/50">
          <td colSpan={6} className="px-2 py-2 space-y-2">
            {victim.description && (
              <div className="text-[11px] text-text-secondary whitespace-pre-wrap">{victim.description}</div>
            )}
            {victim.leak_url && (
              <a href={victim.leak_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1">
                <ExternalLink size={10} /> Leak post
              </a>
            )}
            {victim.supply_chain_hits.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-rose mb-0.5">Supply-chain exposure</div>
                <ul className="text-[10px] space-y-0.5">
                  {victim.supply_chain_hits.map(h => (
                    <li key={h.service_id}>
                      <Link to="/app/services" className="text-text-primary hover:text-blue-light hover:underline">
                        · {h.service_name}
                      </Link>
                      <span className="text-text-muted ml-1">({h.match_kind})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {victim.carrier_advisories.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-purple mb-0.5">Carrier advisories</div>
                <ul className="text-[10px] space-y-0.5">
                  {victim.carrier_advisories.map((a, i) => (
                    <li key={`${a.carrier}-${i}`}>
                      <a href={a.url} target="_blank" rel="noreferrer" className="text-text-primary hover:text-blue-light hover:underline">
                        · [{a.carrier_name}] {a.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}
